import { formatarData, formatarDataHora, formatarDecimal, formatarInteiro } from '@/lib/formato';
import { primeiraMaiuscula } from '@/lib/texto';

import { RECURSO_DO_CADASTRO, type LinhaDaTrilha } from './tipos';

/**
 * A trilha lida por gente (p.22–23: a palavra da obra vence a palavra do
 * software).
 *
 * O trigger de auditoria grava o recurso como `<schema>.<tabela>` e a linha
 * inteira em jsonb, com o nome de cada coluna e o valor como o banco o guarda:
 * data em ISO, identificador em uuid, situacao em minuscula. Nada disso e
 * errado no banco — e so ilegivel na tela. A traducao mora aqui, fora do
 * componente, para a lista, o filtro e o painel falarem igual.
 */

/**
 * Todo recurso que o banco audita, como a tela o chama.
 *
 * As seis primeiras entradas saem de `RECURSO_DO_CADASTRO`, e nao de uma
 * segunda lista: duas copias divergiriam no primeiro cadastro novo. As outras
 * sao as tabelas com `create trigger trilha` nas migracoes, mais os registros
 * de acesso (convite, senha, sessao). Tabela nova auditada precisa entrar aqui;
 * enquanto nao entra, aparece pelo nome da tabela, sem o esquema.
 */
export const NOME_DO_RECURSO: Readonly<Record<string, string>> = {
  [RECURSO_DO_CADASTRO.filiais]: 'Filiais',
  [RECURSO_DO_CADASTRO.clientes]: 'Clientes',
  [RECURSO_DO_CADASTRO.fornecedores]: 'Fornecedores',
  [RECURSO_DO_CADASTRO['centros-de-custo']]: 'Centros de custo',
  [RECURSO_DO_CADASTRO.obras]: 'Obras e locais',
  [RECURSO_DO_CADASTRO.contratos]: 'Contratos',
  'org.empresa': 'Empresa',
  'org.configuracao_da_empresa': 'Configuração da empresa',
  'org.turno': 'Turnos',
  /* ADR-008: a correção de um lançamento. O nome é o que a pessoa
     procura na trilha — "estorno", e não "org.estorno". */
  'org.estorno': 'Estornos de lançamento',
  /* 0059: o lançamento que a plataforma excluiu, guardado inteiro. */
  'org.exclusao': 'Lançamentos excluídos pela plataforma',
  'org.papel': 'Papéis',
  'org.permissao': 'Permissões',
  'org.papel_permissao': 'Permissões de papel',
  'org.usuario': 'Pessoas',
  'org.usuario_papel': 'Papéis de pessoa',
  'org.alcada': 'Alçadas',

  'ativos.equipamento': 'Equipamentos',
  'ativos.fabricante': 'Fabricantes',
  'ativos.modelo': 'Modelos de equipamento',
  'ativos.configuracao': 'Configurações de modelo',
  'ativos.alocacao': 'Alocações de equipamento',
  'ativos.situacao': 'Situação de equipamento',
  'ativos.foto_do_equipamento': 'Fotos de equipamento',

  'comb.posto': 'Postos de combustível',
  'comb.tanque': 'Tanques',
  'comb.medicao_tanque': 'Medições de tanque',
  'comb.movimento_tanque': 'Movimentos de tanque',
  'comb.ajuste_de_tanque': 'Pedidos de ajuste de tanque',
  'comb.transferencia': 'Transferências entre tanques',
  'comb.ordem_abastecimento': 'Ordens de abastecimento',
  'comb.abastecimento': 'Abastecimentos',
  'comb.cobranca': 'Cobranças de combustível',
  'comb.aplicacao_lubrificante': 'Aplicações de lubrificante',
  'comb.folha_de_campo': 'Folhas de campo do abastecimento',
  'comb.leitura_de_folha': 'Leituras de folha de campo',
  'comb.recusa_do_campo': 'Lançamentos do campo recusados',
  'ativos.tipo_de_equipamento': 'Tipos de máquina',
  'org.prefixo_de_codigo': 'Prefixos de código',

  /* Modulo 14 · ciclo 2. 'Medicao' aqui e a medicao do CONTRATO, e nao a
     medicao de tanque do modulo 08 — por isso o nome escrito diz de qual se
     trata: as duas aparecem na mesma lista de auditoria. */
  'rec.regra_do_contrato': 'Regras de valor do contrato',
  'rec.medicao': 'Medições de contrato',
  'rec.item_da_medicao': 'Itens da medição de contrato',
  'rec.documento': 'Documentos de faturamento',

  /* Modulo 09 · ciclo 2. "Pedido" aqui e o pedido de COMPRA, e nao a ordem de
     abastecimento do modulo 08 nem a O.S. do 06 — as tres aparecem na mesma
     lista de auditoria, e o nome escrito e o que as separa. */
  'compras.requisicao': 'Requisições de compra',
  'compras.pedido': 'Pedidos de compra',
  'compras.item_do_pedido': 'Itens do pedido de compra',
  'compras.recebimento': 'Recebimentos de compra',

  /* Modulo 10 · ciclo 2. "Modelo" aqui e o CHECKLIST, e nao o modelo de
     equipamento do modulo 02 — por isso o nome escrito diz de qual se trata.
     "Resposta" e a do item inspecionado, e nao a do assistente de IA. */
  'insp.modelo': 'Checklists de inspeção',
  'insp.item_do_modelo': 'Itens do checklist',
  'insp.inspecao': 'Inspeções feitas',
  'insp.resposta': 'Respostas da inspeção',
  'insp.ocorrencia': 'Ocorrências de inspeção',
  'insp.evidencia': 'Evidências de ocorrência',

  /* Modulo 16 · ciclo 2. "Documento" aqui e o de PESSOA ou de maquina — CNH,
     ASO, seguro —, e nao o documento tecnico do modulo 12 nem o de
     faturamento do 14. As tres aparecem na mesma lista de auditoria. */
  'pess.tipo_de_documento': 'Tipos de documento',
  'pess.documento': 'Documentos de pessoa e de equipamento',
  'pess.aptidao': 'Autorizações para operar',
  'pess.exigencia': 'Exigências do cliente',
  /* 0095: a funcao, quem a exerce e o valor-hora dela (parametro de custo). */
  'pess.funcao': 'Funções',
  'pess.funcao_da_pessoa': 'Funções das pessoas',
  'org.valor_hora_da_funcao': 'Valor-hora das funções',
  /* 0096: a margem completa — a depreciação da máquina e o livro do custo indireto. */
  'ativos.depreciacao_do_equipamento': 'Depreciação das máquinas',
  'org.custo_indireto': 'Custo indireto',
  /* 0090: o arquivo anexado ao documento (a foto da CNH, o PDF do ASO). */
  'pess.anexo_do_documento': 'Arquivos dos documentos',
  /* 0100: o grupo do piloto (quem pôs e quem tirou) e a contagem de inventário. */
  'org.maquina_do_piloto': 'Máquinas do piloto',
  'org.pessoa_do_piloto': 'Pessoas do piloto',
  'estoq.contagem': 'Contagens de inventário',
  /* 0103: a telemetria (módulo 17) — a integração, a chave, o dispositivo,
     o vínculo com a máquina, o lote recebido, o tratamento da fila e as regras. */
  'telem.fonte': 'Integrações de telemetria',
  'telem.chave': 'Chaves das integrações',
  'telem.dispositivo': 'Dispositivos de telemetria',
  'telem.vinculo': 'Dispositivos nas máquinas',
  'telem.lote': 'Lotes recebidos da telemetria',
  'telem.tratamento': 'Tratamentos da fila da telemetria',
  'telem.parametro': 'Regras da telemetria',
  /* 0102: o plano de contas gerencial (módulo 15), a classificação das
     origens e a validação da contabilidade. */
  'fin.conta_gerencial': 'Contas do plano gerencial',
  'fin.classificacao': 'Classificação das origens na DRE',
  'fin.validacao_do_plano': 'Validações do plano pela contabilidade',
  /* 0104: as integrações com o ERP (módulo 04) — o sistema externo, a matriz
     de sistema mestre, a correspondência, o lote, o retorno e a divergência. */
  'integ.sistema_externo': 'Sistemas externos',
  'integ.mestre_da_entidade': 'Matriz de sistema mestre',
  'integ.correspondencia': 'Correspondências com sistemas externos',
  'integ.lote': 'Lotes de integração',
  'integ.retorno': 'Retornos de sistemas externos',
  'integ.divergencia_de_troca': 'Divergências da troca com o ERP',
  'integ.titulo': 'Títulos a pagar e a receber',
  'integ.baixa': 'Baixas de título',
  'integ.nota_fiscal': 'Notas como referência',
  'integ.escrita_em_contingencia': 'Escritas em contingência',
  /* 0106: o orçamento e planejamento (módulo 19) — a versão, as premissas, as
     linhas e os valores, os cenários e a demanda, a explicação do desvio, o
     saldo informado e os limiares. */
  'orc.parametro': 'Limiares do desvio do orçamento',
  'orc.orcamento': 'Versões do orçamento',
  'orc.premissa': 'Premissas do orçamento',
  'orc.linha': 'Linhas do orçamento',
  'orc.valor_da_linha': 'Valores mensais do orçamento',
  'orc.cenario': 'Cenários do orçamento',
  'orc.hipotese': 'Demandas dos cenários',
  'orc.explicacao_do_desvio': 'Explicações de desvio do orçamento',
  'orc.saldo_de_caixa_informado': 'Saldos de caixa informados',
  'estrat.parametro': 'Parâmetros da renovação da frota',
  'estrat.estudo': 'Estudos de renovação',
  'estrat.premissa_do_estudo': 'Premissas dos estudos de renovação',
  'estrat.criterio': 'Critérios de priorização dos investimentos',
  'estrat.proposta': 'Propostas de investimento',
  'estrat.nota_da_proposta': 'Notas das propostas de investimento',
  /* 0109: os fatos que a conta da tela registrou para o aviso. */
  'orc.desvio_observado': 'Desvios do orçamento levados ao aviso',
  'estrat.candidata_observada': 'Máquinas marcadas candidatas à renovação',
  /* 0105: o histórico de falhas e a preditiva (módulo 18) — o catálogo, a
     classificação da corretiva e a sugestão da IA, a investigação, o limite
     do fabricante, o modelo, a rodada, a previsão e a proposta técnica. */
  'manut.parametro_preditivo': 'Base mínima da preditiva',
  'manut.componente': 'Componentes do catálogo de falhas',
  'manut.modo_de_falha': 'Modos de falha',
  'oficina.sugestao_de_falha': 'Sugestões da IA para a falha',
  'oficina.falha_da_ordem': 'Classificações de falha',
  'manut.investigacao': 'Investigações de recorrência',
  'manut.evidencia_da_investigacao': 'Evidências das investigações',
  'manut.limiar_de_sinal': 'Limites de sinal do fabricante',
  'manut.modelo_preditivo': 'Modelos preditivos',
  'manut.rodada_do_modelo': 'Rodadas dos modelos preditivos',
  'manut.previsao': 'Previsões',
  'manut.proposta_tecnica': 'Propostas técnicas',

  'estoq.produto': 'Produtos do estoque',
  'estoq.deposito': 'Depósitos',
  'estoq.posicao': 'Saldos de estoque',
  'estoq.movimento': 'Movimentos de estoque',
  'estoq.reserva': 'Reservas de estoque',
  /* 0088: a unidade de compra e a aplicação validada do item. */
  'estoq.conversao_de_unidade': 'Unidades de compra dos itens',
  'estoq.aplicacao': 'Aplicações dos itens',

  'manut.plano': 'Planos de manutenção',
  'manut.tarefa': 'Tarefas de plano de manutenção',
  'manut.item_da_tarefa': 'Itens de tarefa de manutenção',
  'manut.realizacao': 'Manutenções realizadas',

  'oficina.ordem': 'Ordens de serviço',
  'oficina.etapa_da_ordem': 'Etapas de O.S.',
  'oficina.tarefa_da_ordem': 'Tarefas de O.S.',
  'oficina.mao_de_obra': 'Mão de obra de O.S.',
  'oficina.peca': 'Peças de O.S.',
  'oficina.servico_externo': 'Serviços externos de O.S.',
  'oficina.anexo_da_ordem': 'Anexos de O.S.',

  'oper.leitura_medidor': 'Leituras de horímetro e hodômetro',
  'oper.apontamento': 'Apontamentos de horas',
  'oper.pacote_levado': 'Saídas do pacote para a obra',
  'oper.intervalo_do_apontamento': 'Intervalos do turno',
  'oper.motivo_de_parada': 'Motivos de parada',
  'oper.folha_de_turno': 'Folhas de turno',
  'oper.leitura_de_turno': 'Leituras de folha de turno',
  'oper.hora_reconhecida': 'Horas reconhecidas',
  'oper.divergencia': 'Divergências de horas',
  'oper.lote_importacao': 'Importações de horas',
  'oper.linha_importada': 'Linhas importadas',

  'tecnico.documento': 'Documentos da biblioteca técnica',
  'tecnico.aplicacao': 'Ligações de documento',
  'tecnico.pergunta': 'Perguntas à biblioteca técnica',
  'tecnico.utilidade_da_resposta': 'Utilidade das respostas da IA',
  'tecnico.caso_de_avaliacao': 'Casos de avaliação da IA',
  'org.conferencia_de_historia': 'Conferências das histórias de usuário',
  'ativos.filial_da_obra_atual': 'Filial da obra atual de cada máquina',
  'tecnico.rodada_de_avaliacao': 'Rodadas de avaliação da IA',
  'tecnico.resultado_de_avaliacao': 'Resultados da avaliação da IA',

  'infra.convite': 'Convites de acesso',
  'infra.credencial': 'Senhas',
  'infra.sessao': 'Sessões',
  'infra.segundo_fator': 'Segundo fator',
  'suporte da plataforma': 'Acesso de suporte da plataforma',
};

/** O recurso como a tela o chama; o desconhecido aparece pelo nome da tabela, sem o esquema. */
export function recursoLegivel(recurso: string): string {
  const conhecido = NOME_DO_RECURSO[recurso];
  if (conhecido) return conhecido;
  const tabela = recurso.includes('.') ? recurso.slice(recurso.indexOf('.') + 1) : recurso;
  return primeiraMaiuscula(comAcento(tabela.replaceAll('_', ' ')));
}

/**
 * As palavras que as colunas escrevem sem acento, por convencao do banco. A
 * lista cobre o vocabulario das migracoes; palavra fora dela aparece como esta.
 */
const ACENTO: Readonly<Record<string, string>> = {
  alcada: 'alçada',
  alocacao: 'alocação',
  aplicacao: 'aplicação',
  area: 'área',
  codigo: 'código',
  combustivel: 'combustível',
  conclusao: 'conclusão',
  configuracao: 'configuração',
  criterio: 'critério',
  deposito: 'depósito',
  descricao: 'descrição',
  diaria: 'diária',
  distancia: 'distância',
  divergencia: 'divergência',
  emissao: 'emissão',
  endereco: 'endereço',
  historico: 'histórico',
  hodometro: 'hodômetro',
  horario: 'horário',
  horimetro: 'horímetro',
  importacao: 'importação',
  inicio: 'início',
  lubrificacao: 'lubrificação',
  mao: 'mão',
  manutencao: 'manutenção',
  maximo: 'máximo',
  medicao: 'medição',
  minimo: 'mínimo',
  municipio: 'município',
  numero: 'número',
  observacao: 'observação',
  operacao: 'operação',
  orcamento: 'orçamento',
  pagina: 'página',
  paginas: 'páginas',
  papeis: 'papéis',
  periodo: 'período',
  permissao: 'permissão',
  posicao: 'posição',
  preco: 'preço',
  previsao: 'previsão',
  proxima: 'próxima',
  proximo: 'próximo',
  prontuario: 'prontuário',
  realizacao: 'realização',
  responsavel: 'responsável',
  restricao: 'restrição',
  revisao: 'revisão',
  saida: 'saída',
  servico: 'serviço',
  situacao: 'situação',
  solicitacao: 'solicitação',
  tecnico: 'técnico',
  ultima: 'última',
  ultimo: 'último',
  usuario: 'pessoa',
  validacao: 'validação',
  valido: 'válido',
  veiculo: 'veículo',
  versao: 'versão',
  vigencia: 'vigência',
};

function comAcento(frase: string): string {
  return frase
    .split(' ')
    .map((palavra) => ACENTO[palavra] ?? palavra)
    .join(' ');
}

/** Colunas cujo nome, traduzido palavra a palavra, diria outra coisa. */
const NOME_PROPRIO: Readonly<Record<string, string>> = {
  id: 'Identificador',
  empresa_id: 'Empresa',
  ativo: 'Em uso',
  sistema: 'Pessoa do sistema',
};

/**
 * O nome da coluna em palavras: `publicado_em` vira "Publicado em",
 * `revisao_nota` vira "Revisão nota", `cliente_id` vira "Cliente".
 */
export function nomeDoCampo(coluna: string): string {
  const proprio = NOME_PROPRIO[coluna];
  if (proprio) return proprio;
  const semId = coluna.endsWith('_id') ? coluna.slice(0, -3) : coluna;
  return primeiraMaiuscula(comAcento(semId.replaceAll('_', ' ')));
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DATA_HORA = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/;
const DATA = /^\d{4}-\d{2}-\d{2}$/;
/** Valor de lista fechada, como o banco o guarda: `rascunho`, `ordem_servico`. */
const PALAVRA_DE_LISTA = /^[a-z]+(_[a-z]+)*$/;

/**
 * Um valor da linha gravada, como a tela o mostra: data em dd/mm/aaaa · 24 h
 * (p.12), numero em pt-BR, sim e nao por extenso, valor de lista com inicial
 * maiuscula. O identificador continua sendo o identificador — o nome da coisa
 * a que ele aponta nao esta na linha gravada —, e vem marcado para a tela o
 * escrever em monoespacada.
 */
/* AS FAIXAS DO BANCO (robos de 20/10/2026, rodada 2): `tstzrange` e
   `daterange` chegam a trilha como o texto do Postgres —
   `["2026-10-20 15:28:41.302523-03",)` —, e a tela mostrava isso cru. */
const FAIXA = /^([[(])"?([^",]*)"?,"?([^",]*)"?([\])])$/;
const FUSO_DA_OPERACAO = 'America/Sao_Paulo';
const DIA_NA_OPERACAO = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: FUSO_DA_OPERACAO });
const HORA_NA_OPERACAO = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: FUSO_DA_OPERACAO });

/** Uma ponta da faixa: o instante no fuso da operacao, ou o dia. Nula se nao for nenhum dos dois. */
function pontaLegivel(texto: string, fechaNoDiaAnterior: boolean): string | null {
  if (DATA.test(texto)) {
    const [a, m, d] = texto.split('-').map(Number);
    /* O dia exclusivo (`)` no fim de um daterange) e o ultimo dia que vale menos um. */
    const dia = new Date(Date.UTC(a ?? 0, (m ?? 1) - 1, (d ?? 1) - (fechaNoDiaAnterior ? 1 : 0), 12));
    return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' }).format(dia);
  }
  if (DATA_HORA.test(texto)) {
    const iso = texto
      .replace(' ', 'T')
      .replace(/(\.\d{3})\d+/, '$1')
      .replace(/([+-]\d{2})$/, '$1:00');
    const quando = new Date(iso);
    if (Number.isNaN(quando.getTime())) return null;
    return `${DIA_NA_OPERACAO.format(quando)} · ${HORA_NA_OPERACAO.format(quando)}`;
  }
  return null;
}

/**
 * "desde 20/10/2026 · 15:28", "de 01/10/2026 até 24/10/2026", "até …" — a
 * faixa do banco escrita para gente, no fuso da operacao. Nula quando o texto
 * nao e uma faixa.
 */
export function faixaLegivel(texto: string): string | null {
  if (texto === 'empty') return 'Sem período';
  const f = FAIXA.exec(texto.trim());
  if (!f) return null;
  const [, , inicio = '', fim = '', fecha = ')'] = f;
  const de = inicio === '' ? null : pontaLegivel(inicio, false);
  const ate = fim === '' ? null : pontaLegivel(fim, fecha === ')' && DATA.test(fim));
  if ((inicio !== '' && de === null) || (fim !== '' && ate === null)) return null;
  if (de && ate) return `de ${de} até ${ate}`;
  if (de) return `desde ${de}`;
  if (ate) return `até ${ate}`;
  return 'Sem limite';
}

export function valorLegivel(valor: unknown): { readonly texto: string; readonly identificador: boolean } {
  const so = (texto: string) => ({ texto, identificador: false });
  if (valor === null || valor === undefined) return so('');
  if (typeof valor === 'boolean') return so(valor ? 'Sim' : 'Não');
  if (typeof valor === 'number') return so(Number.isInteger(valor) ? formatarInteiro(valor) : formatarDecimal(valor, 2));
  if (typeof valor === 'string') {
    const limpo = valor.trim();
    if (UUID.test(limpo)) return { texto: limpo, identificador: true };
    const faixa = faixaLegivel(limpo);
    if (faixa !== null) return so(faixa);
    if (DATA_HORA.test(limpo)) {
      const quando = new Date(limpo.replace(' ', 'T'));
      if (!Number.isNaN(quando.getTime())) return so(formatarDataHora(quando));
    }
    if (DATA.test(limpo)) return so(formatarData(new Date(`${limpo}T00:00:00`)));
    if (PALAVRA_DE_LISTA.test(limpo)) return so(primeiraMaiuscula(comAcento(limpo.replaceAll('_', ' '))));
    return so(valor);
  }
  if (Array.isArray(valor)) return so(valor.map((item) => valorLegivel(item).texto).join(', '));
  return so(JSON.stringify(valor));
}

/**
 * O motivo da linha. Quando ninguem declarou motivo, o `detalhe` do registro de
 * acesso diz o que houve — mas escrito para o programa: a leitura do prontuario
 * grava so a marca `prontuario`. Aqui ele vira frase.
 */
export function motivoDaLinha(linha: Pick<LinhaDaTrilha, 'motivo' | 'detalhe'>): string | null {
  if (linha.motivo) return linha.motivo;
  const detalhe = linha.detalhe?.trim() ?? '';
  if (detalhe === '') return null;
  if (detalhe === 'prontuario') return 'Leitura do prontuário';
  return primeiraMaiuscula(detalhe.replace(/\bprontuario\b/g, 'prontuário'));
}
